import { useEffect, useState, useMemo } from 'react';
import {
  Tabs,
  Card,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Table,
  Tag,
  Typography,
  Space,
  notification,
  Modal,
  Descriptions,
  Divider,
  Popconfirm,
  Tooltip,
  message
} from 'antd';
import {
  ShopOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  GlobalOutlined,
  CloseCircleOutlined,
  ExportOutlined,
  EyeOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { useConfig } from '../../../contexts/ConfigContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { useRegister, defaultMovementValues } from '../../../contexts/RegisterContext';
import { IMovementRS } from '../../../models/storageModel';
import { contentLayout, contentPath, modules } from '../../../config/pages';
import useAuth from '../../../hooks/useAuth';
import { useStoreFetch, useItems } from '../../../hooks/useInventory';
import { useEcommerceStores, useEcommerceOrders, useEcommerceStoreItems } from '../../../hooks/useEcommerce';
import ecommerceService from '../../../services/ecommerceService';
import { textNumberFormater } from '../../../utils';

const { Title, Text } = Typography;

export default function ViewEcommerceManagement() {
  const history = useHistory();
  const { config, updateConfig } = useConfig();
  const { company } = useCompany();
  const { updateRegister } = useRegister();
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key);

  const [activeTab, setActiveTab] = useState('1');
  const [selectedStore, setSelectedStore] = useState<any | null>(null);
  const [isCreatingStore, setIsCreatingStore] = useState(false);
  const [savingStore, setSavingStore] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);
  const [resolvingContactOrderId, setResolvingContactOrderId] = useState<number | null>(null);

  const [form] = Form.useForm();

  // Obtener almacenes físicos de la compañía mediante hook SWR
  const { data: storehouseData } = useStoreFetch(tokenData, {
    all: true,
    company: company.current?.uid
  });

  const storehouses = useMemo(() => storehouseData?.storehouse || [], [storehouseData]);

  // Obtener tiendas online mediante hook SWR centralizado
  const {
    stores,
    mutate: refetchStores
  } = useEcommerceStores(tokenData, {
    company_id: company.current?.id,
    company: company.current?.uid
  });

  // Obtener catálogo de elementos mediante hook SWR existente de inventario
  const { data: itemsData, isLoading: loadingCatalog } = useItems(tokenData, {
    company: company.current?.uid,
    module: config.module
  });

  const catalogItems = useMemo(() => itemsData?.itemList || [], [itemsData]);

  // Obtener elementos configurados/publicados para la tienda seleccionada
  const {
    items: storeItems,
    isLoading: loadingStoreItems,
    mutate: refetchStoreItems
  } = useEcommerceStoreItems(tokenData, {
    store_id: selectedStore?.id,
    company: company.current?.uid
  });

  // Mapa de element_id => item configurado de la tienda
  const storeItemsMap = useMemo(() => {
    const map = new Map<number, any>();
    (storeItems || []).forEach((item: any) => {
      map.set(item.element_id, item);
    });
    return map;
  }, [storeItems]);

  const publishedCount = useMemo(() => {
    return (storeItems || []).filter((item: any) => item.is_published).length;
  }, [storeItems]);

  // Obtener pedidos web de la tienda seleccionada mediante hook SWR centralizado
  const {
    orders,
    isLoading: loadingOrders,
    mutate: refetchOrders
  } = useEcommerceOrders(tokenData, {
    company_id: company.current?.id,
    store_id: selectedStore?.id,
    company: company.current?.uid
  });

  const usedStorehouseIds = useMemo(() => {
    return new Set((stores || []).map((st: any) => Number(st.storehouse_id)).filter(Boolean));
  }, [stores]);

  const availableStorehouses = useMemo(() => {
    return (storehouses || []).filter((sh: any) => !usedStorehouseIds.has(Number(sh.id)));
  }, [storehouses, usedStorehouseIds]);

  const isEditingExistingStore = Boolean(selectedStore?.id) && !isCreatingStore;

  useEffect(() => {
    if (stores.length > 0 && !selectedStore && !isCreatingStore) {
      setSelectedStore(stores[0]);
      form.setFieldsValue(stores[0]);
    }
  }, [stores, selectedStore, isCreatingStore, form]);

  const handleStartNewStore = () => {
    setIsCreatingStore(true);
    setSelectedStore(null);
    setActiveTab('1');
    form.resetFields();
    form.setFieldsValue({
      name: '',
      slug: '',
      storehouse_id: availableStorehouses.length === 1 ? availableStorehouses[0].id : undefined,
      is_active: true,
      store_mode: 'ORDER_BUILDER',
      visibility_mode: 'PUBLIC',
      description: ''
    });
  };

  const handleCancelNewStore = () => {
    setIsCreatingStore(false);
    if (stores.length > 0) {
      setSelectedStore(stores[0]);
      form.setFieldsValue(stores[0]);
    }
  };

  // Guardar configuración de tienda (crear o actualizar) vía servicio centralizado
  const handleSaveStore = async (values: any) => {
    setSavingStore(true);
    try {
      if (isEditingExistingStore && selectedStore?.id) {
        const editableValues = { ...values }; delete editableValues.slug; delete editableValues.storehouse_id;
        const res = await ecommerceService.updateStore(tokenData, {
          store_id: selectedStore.id,
          company: company.current?.uid,
          store: {
            ...editableValues,
            company_id: company.current?.id
          }
        });
        if (!res?.error && res?.status === 200) {
          notification.success({ message: 'Tienda actualizada exitosamente' });
          if (res.store) {
            setSelectedStore(res.store);
            form.setFieldsValue(res.store);
          }
          await refetchStores();
        } else {
          notification.warning({ message: res?.message || 'No fue posible actualizar la tienda' });
        }
      } else {
        const res = await ecommerceService.createStore(tokenData, {
          ...values,
          company_id: company.current?.id,
          company: company.current?.uid
        });
        if (!res?.error && res?.status === 200) {
          notification.success({ message: 'Tienda creada exitosamente' });
          setIsCreatingStore(false);
          if (res.store) {
            setSelectedStore(res.store);
            form.setFieldsValue(res.store);
          }
          await refetchStores();
        } else {
          notification.warning({ message: res?.message || 'No fue posible crear la tienda' });
        }
      }
    } catch {
      notification.error({ message: 'Error procesando la solicitud' });
    } finally {
      setSavingStore(false);
    }
  };

  // Guardar publicación de un elemento vía servicio centralizado
  const handleTogglePublish = async (record: any, isPublished: boolean) => {
    if (!selectedStore?.id) {
      notification.warning({ message: 'Selecciona una tienda primero' });
      return;
    }
    try {
      const res = await ecommerceService.setStoreItems(tokenData, {
        store_id: selectedStore.id,
        company: company.current?.uid,
        items: [{ element_id: record.id, is_published: isPublished, featured: false }]
      });
      if (!res?.error && res?.status === 200) {
        notification.success({
          message: isPublished ? 'Elemento publicado en tienda online' : 'Elemento despublicado'
        });
        await refetchStoreItems();
      } else {
        notification.error({ message: res?.message || 'Error actualizando publicación' });
      }
    } catch {
      notification.error({ message: 'Error actualizando publicación' });
    }
  };

  // Copiar link de tienda
  const copyStoreUrl = () => {
    if (!selectedStore?.slug) return;
    const url = `${window.location.origin}/s/${selectedStore.slug}`;
    void navigator.clipboard.writeText(url);
    notification.info({ message: 'Enlace copiado al portapapeles', description: url });
  };

  // Actualizar estado de un pedido (ej. Facturado o Cancelado)
  const handleUpdateOrderStatus = async (orderId: number, newStatus: string) => {
    setUpdatingOrderId(orderId);
    try {
      const res = await ecommerceService.updateOrderStatus(tokenData, {
        order_id: orderId,
        status: newStatus,
        company: company.current?.uid
      });
      if (res?.error) {
        message.error(res.message || 'No fue posible actualizar el estado del pedido');
      } else {
        message.success(
          newStatus === 'INVOICED'
            ? 'Pedido aprobado y marcado como facturado exitosamente'
            : newStatus === 'CANCELLED'
            ? 'Pedido cancelado exitosamente'
            : newStatus === 'PENDING_VALIDATION'
            ? 'Pedido restablecido a Por Validar'
            : 'Estado actualizado'
        );
        await refetchOrders();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder({ ...selectedOrder, status: newStatus });
        }
      }
    } catch {
      message.error('Error al actualizar el estado del pedido');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Cargar pedido web directamente en Salidas de Almacén para emitir factura o remisión
  const handleProcessInExit = async (order: any) => {
    if (!order) return;

    let contactId = order.contact_id || order.contacts?.id || 0;
    let contactName = order.submitted_customer_data?.name || order.contacts?.name || 'Cliente Web';
    let contactDocument = order.submitted_customer_data?.document_number || order.contacts?.document_number || '';
    let contactEmail = order.submitted_customer_data?.email || order.contacts?.email || null;

    // Si el pedido no tiene un contacto vinculado en el ERP, resolverlo o crearlo automáticamente
    if (!contactId && tokenData) {
      const hideMsg = message.loading('Verificando y registrando cliente en ERP...', 0);
      try {
        const resolveRes = await ecommerceService.resolveOrderCustomer(tokenData, {
          order_id: order.id,
          order_code: order.order_code,
          company_id: company.current?.id,
          company_uid: company.current?.uid
        });
        hideMsg();
        if (resolveRes?.status === 200 && resolveRes.contact?.id) {
          contactId = resolveRes.contact.id;
          contactName = resolveRes.contact.name || contactName;
          contactDocument = resolveRes.contact.document_number || contactDocument;
          contactEmail = resolveRes.contact.email || resolveRes.contact.electronic_invoice_email || contactEmail;
          message.success(
            resolveRes.action === 'CREATED_NEW'
              ? `Cliente "${contactName}" registrado exitosamente en el ERP`
              : `Cliente "${contactName}" vinculado exitosamente`
          );
          void refetchOrders();
        }
      } catch (err) {
        hideMsg();
        console.warn('Error resolviendo contacto para el pedido web:', err);
      }
    }

    const catalogList = itemsData?.itemList || [];
    const mappedItems = (order.ecommerce_order_items || []).map((it: any, index: number) => {
      const catalogItem = catalogList.find((cat: any) => cat.id === it.element_id);
      const unitPrice = Number(it.unit_price || catalogItem?.sell_price || catalogItem?.price || 0);
      const qty = Number(it.quantity || 1);
      const discount = Number(it.discount_amount || 0);
      const tax = Number(it.tax_amount || 0);
      // El subtotal base de la línea sin impuestos es qty * unitPrice
      const itemSubtotal = qty * unitPrice;
      // El total neto de la línea con impuestos y descuentos
      const netTotal = itemSubtotal + tax - discount;
      const customId = catalogItem?.custom_id || it.item_sku || String(it.element_id);
      const displayVal = catalogItem?.display || (it.register_type === 'SERVICE' ? 'SERVICIO' : 'UND');
      const itemName = it.item_name || catalogItem?.name || 'Producto';

      return {
        id: it.element_id,
        element_id: it.element_id,
        uid: catalogItem?.uid || ('elem-' + it.element_id),
        custom_id: customId,
        key: customId,
        code: customId,
        name: itemName,
        sku: it.item_sku || catalogItem?.sku || '',
        display: displayVal,
        register: index + 1,
        quantity: qty,
        unit_price: unitPrice,
        sell_price: unitPrice,
        discount_rate: 0,
        discount_amount: discount,
        tax_amount: tax,
        total_tax_amount: tax,
        taxes_list: it.taxes || catalogItem?.taxes_list || [],
        module_options: catalogItem?.module_options || [],
        total_amount: itemSubtotal,
        total_amount_full: itemSubtotal + tax,
        total_amount_net: netTotal,
        client_amount: netTotal,
        register_type: it.register_type || catalogItem?.register_type || 'PRODUCT',
        track_inventory: (it.register_type || catalogItem?.register_type) !== 'SERVICE',
        inventory: catalogItem?.inventory
      };
    });

    const calculatedSubtotal = mappedItems.reduce((acc: number, it: any) => acc + (it.total_amount || 0), 0);
    const calculatedTotalTax = mappedItems.reduce((acc: number, it: any) => acc + (it.total_tax_amount || 0), 0);
    const calculatedTotalDiscount = mappedItems.reduce((acc: number, it: any) => acc + (it.discount_amount || 0), 0);
    const calculatedTotalNet = calculatedSubtotal + calculatedTotalTax - calculatedTotalDiscount;
    const orderTotal = Number(order.total_amount) || calculatedTotalNet;

    const pendingMovement: IMovementRS = {
      ...defaultMovementValues,
      id: order.id || -1,
      uid: order.pending_movement_uid || order.order_code || ('mov-web-' + order.id),
      document_number: order.order_code || '',
      reference_document: order.order_code || '',
      document_type: 'SALE_INVOICE',
      origin_channel: 'ECOMMERCE',
      storehouse_from_id: order.storehouse_id || selectedStore?.storehouse_id || undefined,
      storehouse_to_id: null,
      contact_id: contactId,
      contact_name: contactName,
      contact_document: contactDocument,
      contact_email: contactEmail,
      item_list: mappedItems,
      total_amount: calculatedSubtotal,
      total_amount_net: orderTotal,
      total_amount_full: calculatedSubtotal + calculatedTotalTax,
      total_tax_amount: calculatedTotalTax,
      total_discount_amount: calculatedTotalDiscount,
      notes: 'Pedido Web ' + order.order_code + ' - ' + (order.delivery_info?.delivery_method === 'DELIVERY' ? 'Domicilio' : 'Recoger en tienda')
    };

    updateRegister({
      itemListExits: mappedItems,
      currentMovementType: 'SALE_INVOICE',
      currentPending: pendingMovement
    });

    updateConfig({
      forceModule: true,
      module: modules.RegisterExit,
      content: contentLayout[modules.RegisterExit]
    });

    setOrderModalVisible(false);
    history.push(contentPath[modules.RegisterExit]);
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <GlobalOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
            Tienda Online / E-commerce
          </Title>
          <Text type="secondary">Gestiona catálogos digitales, almacenes vinculados y pedidos de clientes externos</Text>
        </div>
        {stores.length > 0 && (
          <Space wrap>
            <Select
              placeholder="Nueva tienda en creación..."
              value={isCreatingStore ? undefined : selectedStore?.id}
              onChange={(storeId) => {
                const st = stores.find((s: any) => s.id === storeId);
                if (st) {
                  setIsCreatingStore(false);
                  setSelectedStore(st);
                  form.setFieldsValue(st);
                }
              }}
              style={{ minWidth: 220 }}
            >
              {stores.map((st: any) => (
                <Select.Option key={st.id} value={st.id}>
                  {st.name}{st.is_active === false ? ' (Inactiva)' : ''}
                </Select.Option>
              ))}
            </Select>

            {!isCreatingStore && (
              <Tooltip
                title={
                  availableStorehouses.length === 0
                    ? 'Todos tus almacenes físicos ya cuentan con una tienda online vinculada.'
                    : 'Crear una nueva tienda online para otro almacén disponible'
                }
              >
                <span>
                  <Button
                    icon={<PlusOutlined />}
                    disabled={availableStorehouses.length === 0}
                    onClick={handleStartNewStore}
                  >
                    Nueva Tienda
                  </Button>
                </span>
              </Tooltip>
            )}

            {!isCreatingStore && selectedStore?.slug && (
              <>
                <Button icon={<CopyOutlined />} onClick={copyStoreUrl}>
                  Copiar Enlace
                </Button>
                <Button
                  type="primary"
                  icon={<ShopOutlined />}
                  onClick={() => window.open(`/s/${selectedStore.slug}`, '_blank')}
                >
                  Abrir Tienda
                </Button>
              </>
            )}
          </Space>
        )}
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* TAB 1: CONFIGURACIÓN DE TIENDA */}
          <Tabs.TabPane
            tab={
              <span>
                <ShopOutlined /> Configuración de Tienda
              </span>
            }
            key="1"
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveStore}
              initialValues={{
                is_active: true,
                store_mode: 'ORDER_BUILDER',
                visibility_mode: 'PUBLIC'
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                <Form.Item
                  label="Nombre de la Tienda"
                  name="name"
                  rules={[{ required: true, message: 'Ingresa el nombre comercial' }]}
                >
                  <Input placeholder="Ej. Ferretería El Roble - Mostrador Online" />
                </Form.Item>

                <Form.Item
                  label="Identificador de URL (Slug)"
                  name="slug"
                  extra={
                    isEditingExistingStore
                      ? 'Fijo tras crear la tienda para conservar enlaces y seguimiento de pedidos.'
                      : undefined
                  }
                  rules={[{ required: true, message: 'Ingresa el slug para la URL' }]}
                >
                  <Input
                    placeholder="ej. el-roble-online"
                    addonBefore="/s/"
                    disabled={isEditingExistingStore}
                  />
                </Form.Item>

                <Form.Item
                  label="Almacén Físico Vinculado"
                  name="storehouse_id"
                  extra={
                    isEditingExistingStore
                      ? 'El almacén vinculado es permanente. Usa "Nueva Tienda" para otro almacén.'
                      : undefined
                  }
                  rules={[{ required: true, message: 'Selecciona el almacén' }]}
                >
                  <Select placeholder="Selecciona la bodega de origen" disabled={isEditingExistingStore}>
                    {storehouses.map((sh: any) => {
                      const alreadyLinked = !isEditingExistingStore && usedStorehouseIds.has(Number(sh.id));
                      return (
                        <Select.Option key={sh.id} value={sh.id} disabled={alreadyLinked}>
                          {sh.name} {sh.alias ? `(${sh.alias})` : ''}{alreadyLinked ? ' — Ya tiene tienda' : ''}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>

                <Form.Item label="Modo Operativo de la Tienda" name="store_mode">
                  <Select>
                    <Select.Option value="ORDER_BUILDER">
                      🛒 Armador de Pedidos (Catálogo + Carrito + Envío de Pedido)
                    </Select.Option>
                    <Select.Option value="PRICE_CATALOG_ONLY">
                      📋 Solo Catálogo de Precios (Consulta sin carrito ni pedidos)
                    </Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item label="Audiencia / Visibilidad" name="visibility_mode">
                  <Select>
                    <Select.Option value="PUBLIC">🌍 Pública (Abierta a clientes en general)</Select.Option>
                    <Select.Option value="INTERNAL_USERS">🔒 Interna (Solo asesores y empleados mayoristas)</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item label="Estado de la Tienda" name="is_active" valuePropName="checked">
                  <Switch checkedChildren="Activa" unCheckedChildren="Inactiva" />
                </Form.Item>
              </div>

              <Form.Item label="Descripción o Bienvenida" name="description">
                <Input.TextArea rows={2} placeholder="Mensaje de bienvenida para los clientes al ingresar..." />
              </Form.Item>

              <Space>
                <Button type="primary" htmlType="submit" loading={savingStore} icon={<CheckCircleOutlined />}>
                  {isEditingExistingStore ? 'Guardar Cambios' : 'Crear Tienda'}
                </Button>
                {isCreatingStore && stores.length > 0 && (
                  <Button onClick={handleCancelNewStore} disabled={savingStore}>
                    Cancelar
                  </Button>
                )}
              </Space>
            </Form>
          </Tabs.TabPane>

          {/* TAB 2: PUBLICACIÓN DE CATÁLOGO */}
          <Tabs.TabPane
            disabled={stores.length === 0 || isCreatingStore}
            tab={
              <span>
                <AppstoreOutlined /> Catálogo Publicado ({publishedCount} de {catalogItems.length} ítems)
              </span>
            }
            key="2"
          >
            <Table
              dataSource={catalogItems}
              rowKey="id"
              loading={loadingCatalog || loadingStoreItems}
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Producto / Servicio',
                  dataIndex: 'name',
                  key: 'name',
                  render: (text, record: any) => (
                    <div>
                      <Text strong>{text}</Text>
                      {record.sku && <div><Text type="secondary" style={{ fontSize: '12px' }}>SKU: {record.sku}</Text></div>}
                    </div>
                  )
                },
                {
                  title: 'Tipo',
                  dataIndex: 'register_type',
                  key: 'register_type',
                  render: (t) => <Tag color={t === 'SERVICE' ? 'purple' : 'blue'}>{t || 'PRODUCTO'}</Tag>
                },
                {
                  title: 'Precio Venta',
                  dataIndex: 'sell_price',
                  key: 'sell_price',
                  render: (v) => textNumberFormater(v || 0)
                },
                {
                  title: 'Publicar en Tienda',
                  key: 'publish',
                  render: (_, record: any) => {
                    const storeItem = storeItemsMap.get(record.id);
                    const isPublished = Boolean(storeItem?.is_published);
                    return (
                      <Switch
                        checked={isPublished}
                        onChange={(checked) => handleTogglePublish(record, checked)}
                      />
                    );
                  }
                }
              ]}
            />
          </Tabs.TabPane>

          {/* TAB 3: PEDIDOS WEB RECIBIDOS */}
          <Tabs.TabPane
            disabled={stores.length === 0 || isCreatingStore}
            tab={
              <span>
                <ShoppingOutlined /> Pedidos Web ({orders.length})
              </span>
            }
            key="3"
          >
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button icon={<ReloadOutlined />} onClick={() => refetchOrders()} loading={loadingOrders}>
                Actualizar Pedidos
              </Button>
            </div>

            <Table
              dataSource={orders}
              rowKey="id"
              loading={loadingOrders}
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Código',
                  dataIndex: 'order_code',
                  key: 'order_code',
                  render: (c) => <Tag color="geekblue" style={{ fontSize: '13px' }}>{c}</Tag>
                },
                {
                  title: 'Fecha',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (d) => new Date(d).toLocaleString()
                },
                {
                  title: 'Cliente',
                  key: 'customer',
                  render: (_, r: any) => (
                    <div>
                      <div>{r.submitted_customer_data?.name || 'Cliente Web'}</div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Doc: {r.submitted_customer_data?.document_number || 'S/N'}
                      </Text>
                      <div style={{ marginTop: '4px' }}>
                        {r.contact_match_status === 'MATCHED_EXISTING' ? (
                          <Tag color="green" icon={<CheckCircleOutlined />}>Registrado</Tag>
                        ) : (
                          <Tag color="orange" icon={<ExclamationCircleOutlined />}>Por Validar</Tag>
                        )}
                      </div>
                    </div>
                  )
                },
                {
                  title: 'Estado',
                  dataIndex: 'status',
                  key: 'status',
                  render: (s) => {
                    const isInv = s === 'INVOICED';
                    const isCanc = s === 'CANCELLED';
                    const color = isInv ? 'green' : isCanc ? 'red' : 'gold';
                    const text = isInv ? 'Facturado' : isCanc ? 'Cancelado' : 'Por Validar';
                    return <Tag color={color}>{text}</Tag>;
                  }
                },
                {
                  title: 'Total',
                  dataIndex: 'total_amount',
                  key: 'total_amount',
                  render: (t) => <Text strong>{textNumberFormater(t || 0)}</Text>
                },
                {
                  title: 'Acciones',
                  key: 'actions',
                  render: (_, r: any) => (
                    <Space>
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedOrder(r);
                          setOrderModalVisible(true);
                        }}
                      >
                        Detalle
                      </Button>
                      {r.status === 'PENDING_VALIDATION' && !r.movement_document_id && (
                        <Tooltip title="Cargar pedido en Salida de Almacén para facturar">
                          <Button
                            size="small"
                            type="primary"
                            icon={<ExportOutlined />}
                            onClick={() => handleProcessInExit(r)}
                          >
                            Facturar Salida
                          </Button>
                        </Tooltip>
                      )}
                      {r.status === 'PENDING_VALIDATION' && (
                        <Tooltip title="Cancelar pedido">
                          <Popconfirm
                            title={`¿Cancelar el pedido ${r.order_code}?`}
                            onConfirm={() => handleUpdateOrderStatus(r.id, 'CANCELLED')}
                            okText="Sí, Cancelar"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                          >
                            <Button
                              size="small"
                              danger
                              icon={<CloseCircleOutlined />}
                              loading={updatingOrderId === r.id}
                            />
                          </Popconfirm>
                        </Tooltip>
                      )}
                    </Space>
                  )
                }
              ]}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Modal de Detalle de Pedido */}
      <Modal
        title={`Detalle de Pedido: ${selectedOrder?.order_code || ''}`}
        open={orderModalVisible}
        onCancel={() => setOrderModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setOrderModalVisible(false)}>
            Cerrar
          </Button>,
          selectedOrder?.tracking_token && selectedStore?.slug && (
            <Button
              key="tracking"
              icon={<EyeOutlined />}
              onClick={() => {
                window.open(`/s/${selectedStore.slug}/order/${selectedOrder.tracking_token}`, '_blank');
              }}
            >
              Ver Seguimiento Público
            </Button>
          ),
          selectedOrder?.status === 'PENDING_VALIDATION' && (
            <Popconfirm
              key="cancel"
              title="¿Estás seguro de cancelar este pedido web?"
              onConfirm={() => handleUpdateOrderStatus(selectedOrder.id, 'CANCELLED')}
              okText="Sí, Cancelar"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button danger loading={updatingOrderId === selectedOrder.id} icon={<CloseCircleOutlined />}>
                Cancelar Pedido
              </Button>
            </Popconfirm>
          ),
          selectedOrder?.status === 'PENDING_VALIDATION' && !selectedOrder?.movement_document_id && (
            <Button
              key="dispatch"
              type="primary"
              icon={<ExportOutlined />}
              onClick={() => handleProcessInExit(selectedOrder)}
            >
              Cargar en Salida de Almacén para Facturar
            </Button>
          ),
          selectedOrder?.status === 'INVOICED' && !selectedOrder?.movement_document_id && (
            <Button
              key="revert"
              loading={updatingOrderId === selectedOrder.id}
              onClick={() => handleUpdateOrderStatus(selectedOrder.id, 'PENDING_VALIDATION')}
            >
              Restablecer a "Por Validar"
            </Button>
          ),
          selectedOrder?.status === 'INVOICED' && !selectedOrder?.movement_document_id && (
            <Button
              key="dispatch-retry"
              type="primary"
              icon={<ExportOutlined />}
              onClick={() => handleProcessInExit(selectedOrder)}
            >
              Completar Facturación en Salidas
            </Button>
          )
        ]}
        width={760}
      >
        {selectedOrder && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Código">{selectedOrder.order_code}</Descriptions.Item>
              <Descriptions.Item label="Estado">
                {selectedOrder.status === 'INVOICED' ? (
                  <Tag color="green">Facturado / Aprobado</Tag>
                ) : selectedOrder.status === 'CANCELLED' ? (
                  <Tag color="red">Cancelado</Tag>
                ) : (
                  <Tag color="gold">Por Validar</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Cliente">
                <Space direction="vertical" size={2}>
                  <Text strong>{selectedOrder.submitted_customer_data?.name || selectedOrder.contacts?.name || 'Cliente Web'}</Text>
                  {selectedOrder.contact_id || selectedOrder.contacts?.id ? (
                    <Tag color="green">Registrado en ERP (ID: {selectedOrder.contact_id || selectedOrder.contacts?.id})</Tag>
                  ) : (
                    <Space size={4}>
                      <Tag color="orange">Web (No registrado)</Tag>
                      <Button
                        size="small"
                        type="link"
                        loading={resolvingContactOrderId === selectedOrder.id}
                        style={{ padding: 0 }}
                        onClick={async () => {
                          setResolvingContactOrderId(selectedOrder.id);
                          try {
                            const res = await ecommerceService.resolveOrderCustomer(tokenData, {
                              order_id: selectedOrder.id,
                              order_code: selectedOrder.order_code,
                              company_id: company.current?.id,
                              company_uid: company.current?.uid
                            });
                            if (res?.status === 200 && res.contact?.id) {
                              message.success(`Cliente "${res.contact.name}" registrado y vinculado en ERP`);
                              setSelectedOrder({
                                ...selectedOrder,
                                contact_id: res.contact.id,
                                contact_match_status: 'MATCHED_EXISTING',
                                contacts: res.contact
                              });
                              void refetchOrders();
                            } else {
                              message.error(res?.message || 'No fue posible registrar el cliente');
                            }
                          } catch {
                            message.error('Error al registrar el cliente');
                          } finally {
                            setResolvingContactOrderId(null);
                          }
                        }}
                      >
                        Registrar en ERP
                      </Button>
                    </Space>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Documento">{selectedOrder.submitted_customer_data?.document_number || 'S/N'}</Descriptions.Item>
              <Descriptions.Item label="Teléfono">{selectedOrder.submitted_customer_data?.phone || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedOrder.submitted_customer_data?.email || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Entrega" span={2}>
                {selectedOrder.delivery_info?.delivery_method === 'DELIVERY' ? 'Domicilio' : 'Recogida en Tienda'} -{' '}
                {selectedOrder.delivery_info?.address || ''}
              </Descriptions.Item>
              <Descriptions.Item label="Notas" span={2}>{selectedOrder.notes || 'Sin notas'}</Descriptions.Item>
            </Descriptions>

            <Divider>Artículos del Pedido</Divider>
            <Table
              dataSource={selectedOrder.ecommerce_order_items || []}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                { title: 'Ítem', dataIndex: 'item_name', render: (t) => <Text strong>{t || 'Producto'}</Text> },
                { title: 'Cantidad', dataIndex: 'quantity', align: 'center' },
                { title: 'Precio Unitario', dataIndex: 'unit_price', align: 'right', render: (v) => textNumberFormater(v || 0) },
                { title: 'Total', dataIndex: 'total_amount', align: 'right', render: (v) => textNumberFormater(v || 0) }
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
